import React from 'react';
import { useLocation } from 'react-router-dom';
import { CollapseIcon } from './icons';
import { ArcIconButton, ArcStack } from '@src/core/ui';
import { useCameraLayout } from './camera-layout-context';
import CameraView from './index';
import PipMapCollapseButton from './pip-map-collapse-button';
import { pipCollapsedExpandButtonSx, pipOverlaySx } from './pip-overlay-sx';

interface CameraMapLayoutProps {
  children: React.ReactNode;
}

// Routes where the user is planning an order/mission and the camera UI
// should be hidden, even if a drone is selected globally. The underlying
// MediaStream is intentionally NOT torn down — `activeSource` stays in
// GlobalCameraProvider so that returning to a camera-aware route resumes
// playback instantly without a WebRTC reconnect.
const PLANNING_ROUTE_MATCHERS: Array<(path: string) => boolean> = [
  (p) => p === '/app/orders/create' || p === '/app/orders/create/',
  (p) => p.startsWith('/app/orders/update/'),
  (p) => p.startsWith('/app/orders/create-mission/'),
  (p) => p.startsWith('/app/orders/update-mission/'),
];

const isPlanningPath = (pathname: string): boolean =>
  PLANNING_ROUTE_MATCHERS.some((match) => match(pathname));

/**
 * Layout shell that positions the map alongside / under / inside the
 * persistent camera view depending on the current view mode.
 *
 * The camera <video> DOM node is owned by GlobalCameraProvider and
 * teleported into <CameraView /> on mount — that's what keeps the video
 * flicker-free when navigating between feature roots.
 */
const CameraMapLayout: React.FC<CameraMapLayoutProps> = ({ children: map }) => {
  const { viewMode, cameraSource, collapsed, toggleCollapse } = useCameraLayout();
  const { pathname } = useLocation();

  const isPlanningRoute = isPlanningPath(pathname);
  const showCamera = !!cameraSource && !isPlanningRoute;
  const isPip = showCamera && viewMode === 'pip';
  const isSplit = showCamera && viewMode === 'split';
  const isPipCollapsed = isPip && collapsed;

  return (
    <ArcStack
      direction={isSplit ? 'row' : undefined}
      width='100%'
      height='100%'
      position='relative'>

      <ArcStack
        width={isSplit ? '50%' : '100%'}
        height='100%'
        sx={isPip ? (isPipCollapsed ? {
          display: 'none',
        } : pipOverlaySx) : undefined}>
        {isPip && !isPipCollapsed && (
          <PipMapCollapseButton onClick={toggleCollapse} />
        )}
        {isPip
          ? React.cloneElement(map as React.ReactElement, { hideToolbar: true, hideMapControls: true })
          : map}
      </ArcStack>

      {isPipCollapsed && (
        <ArcIconButton
          onClick={toggleCollapse}
          sx={pipCollapsedExpandButtonSx}>
          <CollapseIcon sx={{ transform: 'rotate(180deg)' }} />
        </ArcIconButton>
      )}

      {showCamera && (
        <ArcStack
          width={isPip ? '100%' : isSplit ? '50%' : undefined}
          height={isPip ? '100%' : isSplit ? '100%' : undefined}
          sx={isPip ? {
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 0,
          } : undefined}>
          <CameraView />
        </ArcStack>
      )}
    </ArcStack>
  );
};

export default CameraMapLayout;
